import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  Trophy,
  Briefcase,
  MapPin,
  Plane,
  Sparkles,
} from "lucide-react";
import Seo from "@/components/Seo";
import { DESTINATION_IMAGES } from "@/lib/media";

const BREADCRUMB = [
  { name: "Home", path: "/" },
  { name: "Laos DMC", path: "/laos-dmc" },
];

const SERVICES = [
  {
    icon: Trophy,
    title: "MICE",
    copy:
      "Laos offers MICE planners something increasingly rare — authentic exclusivity. Our Laos MICE programmes are designed for boutique incentive groups and senior leadership retreats seeking meaningful experiences away from mainstream circuits. Luang Prabang, a UNESCO World Heritage city, serves as our primary MICE base, with stunning riverside venues, intimate luxury lodges such as Rosewood Luang Prabang and Amantaka, and access to unique experiences like private alms-giving ceremonies, monks' blessing rituals, and exclusive Mekong dinner cruises. Vientiane offers modern conference infrastructure for larger groups. All programmes include dedicated on-ground MICE coordinators.",
  },
  {
    icon: Briefcase,
    title: "Group Leisure",
    copy:
      "Laos rewards the curious group traveller with experiences that feel genuinely unhurried and unspoiled. Our Laos group leisure programmes cover Luang Prabang's golden temples and night markets, the sacred Pak Ou Caves, Kuang Si waterfall excursions, Vientiane's French-colonial boulevards, and the dramatic Vang Vieng landscape. Programmes are available as 5N/6D or 7N/8D formats, combining Laos with neighbouring Vietnam or Thailand for multi-country group itineraries. Guided, comfortable, and culturally immersive — ideal for heritage lovers, photography groups, and senior travellers.",
  },
  {
    icon: Sparkles,
    title: "FIT",
    copy:
      "Laos is perfect for the FIT traveller who values tranquillity, authenticity, and cultural depth over crowds and commercialism. Our Laos FIT programmes range from a focused 4-night Luang Prabang experience to a comprehensive 9-night Laos trail covering Vientiane, Vang Vieng, and Luang Prabang. We also offer seamless multi-country FIT combinations pairing Laos with northern Vietnam or Chiang Mai, Thailand. Every itinerary is custom-built with private transfers, locally knowledgeable guides, curated boutique or luxury hotel options, and flexible day-by-day touring schedules.",
  },
];

const HIGHLIGHTS = [
  "Luang Prabang UNESCO old town",
  "Mekong sunset cruises",
  "Alms-giving ceremonies",
  "Elephant sanctuary visits",
  "Kuang Si waterfall excursions",
  "Local craft workshops",
];

const VENUES = [
  { name: "Rosewood Luang Prabang", note: "Hill-side luxury tents & private events lawn" },
  { name: "Amantaka", note: "All-suite Aman property — boutique incentive favourite" },
  { name: "Sofitel Luang Prabang", note: "Colonial-heritage ballrooms, intimate galas" },
  { name: "Crowne Plaza Vientiane", note: "Capital's leading MICE hotel" },
  { name: "Settha Palace Hotel, Vientiane", note: "Historic French-colonial venue" },
  { name: "Mekong River Cruise Vessels", note: "Floating gala-dinner platforms" },
];

const FAQS = [
  {
    q: "What size of group does Trawelldmc handle in Laos?",
    a: "Laos is best suited for boutique groups of 20–150 pax. Luang Prabang's intimate hotel inventory rewards programmes that prize exclusivity and depth over scale. For 200+ pax, Vientiane works better, or we split the programme across both cities.",
  },
  {
    q: "How accessible is Laos for international groups?",
    a: "Luang Prabang and Vientiane are well-connected via Bangkok, Hanoi, Singapore and Chiang Mai. We commonly pair Laos with northern Vietnam or Thailand for multi-country itineraries — a single visa pre-clearance, one operations team, seamless handoff.",
  },
  {
    q: "What experiences make Laos memorable for incentive travel?",
    a: "Private dawn alms-giving ceremonies, a monks' blessing ritual at a riverside temple, exclusive Mekong sunset gala cruises, and the sheer absence of crowds. For groups that already attended every standard incentive destination, Laos delivers content nobody else can match.",
  },
];

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function LaosDmc() {
  return (
    <div data-testid="laos-dmc-page">
      <Seo
        path="/laos-dmc"
        title="Laos DMC | Group & Leisure Tours | Trawelldmc"
        description="DMC in Laos — Luang Prabang, Vientiane & beyond. Boutique MICE, group leisure & FIT travel programmes. Request a Laos proposal."
        breadcrumb={BREADCRUMB}
        extraJsonLd={FAQ_JSONLD}
      />

      <section className="relative">
        <img
          src={DESTINATION_IMAGES.laos}
          alt="Luang Prabang sunset — Trawelldmc DMC Laos"
          width="1920"
          height="1080"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(11,25,44,0.92) 0%, rgba(11,25,44,0.55) 100%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-32 md:py-44 text-white">
          <div className="max-w-3xl">
            <div className="overline !text-[#FF5238] mb-5">
              Laos DMC · Luang Prabang · Vientiane
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.06]">
              <span className="text-[#FF5238]">Laos</span> Destination Management Company (DMC) & Boutique MICE Specialist
            </h1>
            <p className="text-white/80 mt-6 text-sm tracking-[0.2em] uppercase font-medium">
              Unspoiled, Serene & Unforgettable
            </p>
            <p className="text-white/80 mt-6 text-lg max-w-2xl leading-relaxed">
              Boutique MICE, group leisure and FIT programmes across Laos's
              UNESCO heritage cities — engineered end-to-end by our local team.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/inquiry?destination=Laos"
                className="btn-primary"
                data-testid="laos-cta-proposal"
              >
                Get a Laos proposal <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/quote" className="btn-secondary" data-testid="laos-cta-ai-quote">
                Instant AI estimate
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* WHY LAOS / OVERVIEW */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline mb-4">Why Laos</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Southeast Asia's <br />best-kept secret.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-slate-700 text-lg leading-relaxed">
            <p>
              Laos is Southeast Asia's best-kept secret — a landlocked jewel of
              golden temples, tranquil rivers, and untouched landscapes.
              Trawelldmc brings Laos to life for discerning group travellers
              and incentive planners seeking something truly off the beaten
              path.
            </p>
            <p>
              Our programmes across Luang Prabang, Vientiane, and the 4,000
              Islands blend authentic cultural encounters — alms-giving
              ceremonies, Mekong sunset cruises, elephant sanctuaries — with
              boutique luxury. For groups seeking exclusivity without the
              crowds, Laos is the destination that consistently surprises and
              delights.
            </p>
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <section className="bg-[#FDFCF8] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="overline mb-4">Top Experiences</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Laos highlights.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {HIGHLIGHTS.map((h) => (
              <div key={h} className="card-flat p-6 flex items-start gap-3">
                <MapPin className="h-5 w-5 text-[#FF5238] flex-shrink-0 mt-0.5" />
                <span className="text-[#0B192C] font-medium">{h}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="mb-16 max-w-2xl">
          <div className="overline mb-4">What we deliver</div>
          <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
            Our Laos services.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {SERVICES.map((s) => (
            <div key={s.title} className="card-flat p-8" data-testid={`laos-service-${s.title.toLowerCase().replace(/ /g, "-")}`}>
              <s.icon className="h-8 w-8 text-[#FF5238]" />
              <h3 className="font-heading text-xl font-medium tracking-tight text-[#0B192C] mt-5">
                {s.title}
              </h3>
              <p className="text-slate-600 mt-3 leading-relaxed text-sm">{s.copy}</p>
            </div>
          ))}
        </div>
      </section>

      {/* VENUES */}
      <section className="bg-[#0B192C] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="overline !text-[#FF5238] mb-4">Top venues</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight">
              Hotels & venues <br />
              <span className="text-[#FF5238]">we partner with.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6">
            {VENUES.map((v) => (
              <div key={v.name} className="border-t border-white/15 pt-5">
                <div className="flex gap-3 items-start">
                  <Building2 className="h-5 w-5 text-[#FF5238] flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-heading text-lg font-medium">{v.name}</div>
                    <div className="text-sm text-white/60 mt-1">{v.note}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="mb-16 max-w-2xl">
          <div className="overline mb-4">FAQ</div>
          <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
            Frequently Asked Questions
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
          {FAQS.map((f, i) => (
            <div key={f.q} data-testid={`laos-faq-${i}`}>
              <h3 className="font-heading text-xl font-medium text-[#0B192C] tracking-tight">
                {f.q}
              </h3>
              <p className="text-slate-600 mt-3 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#FDFCF8] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="overline mb-4">Next step</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Ready to plan your <br />
              <span className="text-[#FF5238]">Laos programme?</span>
            </h2>
            <p className="text-slate-600 mt-6 max-w-xl text-lg leading-relaxed">
              Contact Trawelldmc for a custom Laos proposal. We respond within
              24 hours.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-4">
            <Link
              to="/inquiry?destination=Laos"
              className="btn-primary w-full justify-center"
              data-testid="laos-bottom-cta"
            >
              Request a proposal <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" className="btn-secondary w-full justify-center">
              Contact our team
            </Link>
          </div>
        </div>
      </section>

      {/* YOU MAY ALSO LIKE */}
      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-16">
          <div className="overline mb-6">You may also like</div>
          <nav
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            aria-label="Related DMC destination pages"
          >
            <Link to="/cambodia-dmc" className="group flex items-start gap-3" data-testid="laos-link-cambodia">
              <MapPin className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">DMC Cambodia</div>
                <div className="text-sm text-slate-500 mt-1">Siem Reap · Phnom Penh</div>
              </div>
            </Link>
            <Link to="/vietnam-dmc" className="group flex items-start gap-3" data-testid="laos-link-vietnam">
              <MapPin className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">DMC Vietnam</div>
                <div className="text-sm text-slate-500 mt-1">Hanoi · HCMC · Ha Long</div>
              </div>
            </Link>
            <Link to="/thailand-dmc" className="group flex items-start gap-3" data-testid="laos-link-thailand">
              <MapPin className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">DMC Thailand</div>
                <div className="text-sm text-slate-500 mt-1">Bangkok · Phuket · Chiang Mai</div>
              </div>
            </Link>
            <Link to="/" className="group flex items-start gap-3" data-testid="laos-link-home">
              <Plane className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">Trawelldmc Home</div>
                <div className="text-sm text-slate-500 mt-1">All destinations & services</div>
              </div>
            </Link>
          </nav>
        </div>
      </section>
    </div>
  );
}
